import { isDesktop } from '@/consts/device';
import { getAppStore, setAppStore } from '@/store';
import { generationPipeline } from '@/utils/user/generationPipeline';
import { removePresetJsonUrlFromUrl } from '@/utils/ui/removePresetJsonUrlFromUrl';
import { submitWebHook } from '@/requests/magic-flow/submitWebHook';
import { getMediaFromFlowId } from '@/requests/magic-flow/getMediaFromFlowId';
import { changeDriveFileName } from '@/requests/cloud-storage/changeDriveFileName';
import { buildParamsFromInputs } from '@/utils/flow/buildParamsFromInputs';
import { createNotificationConfig } from '@/utils/ui/createNotificationConfig';
import {
  saveGeneration,
  clearGeneration,
} from '@/utils/storage/generationStorage';
import {
  formatTimestamp,
  getExtensionFromResultType,
  getMediaTypeFromString,
  getGenerationErrorMessage,
  sendGenerateFailureAnalytics,
  sendGenerateStartAnalytics,
} from '@/hooks/useGenerate/utils';
import { MediaType } from '@/types';

import type { ContentFile, IInput, Preset, PreviewMediaType } from '@/types';

interface AutoGenerationParams {
  webhookId: string;
  selectedPreset: Preset;
  inputs: IInput[];
  toolId: string;
  folderName: string;
}

interface AutoGenerationCallbacks {
  setGenerationError: (error: string) => void;
  showToast: (message: string, type: 'error' | 'success') => void;
  onReset: () => void;
  onPlaceholderCreated: (uuid: string) => void;
}

/**
 * Runs the auto-generation flow: creates placeholder, submits webhook,
 * polls for result, updates placeholder with real data.
 *
 * Extracted from useAutoGeneration's useEffect to keep the hook lean.
 */
export const runAutoGeneration = async ({
  params,
  callbacks,
}: {
  params: AutoGenerationParams;
  callbacks: AutoGenerationCallbacks;
}) => {
  const { webhookId, selectedPreset, inputs, toolId, folderName } = params;
  const { setGenerationError, showToast, onReset, onPlaceholderCreated } =
    callbacks;

  const autoStartTime = Date.now();

  // Remove presetJsonUrl early so page refresh uses persisted storage for recovery
  removePresetJsonUrlFromUrl();
  setAppStore({
    isPrimaryActionLoading: true,
    isGenerating: true,
    resultFile: null,
    shouldSkipAutoGeneration: true,
  });

  // Create loading placeholder and navigate to My Creations immediately
  const placeholderUuid = `generating-${Date.now()}`;
  const placeholder: ContentFile = {
    uuid: placeholderUuid,
    name: 'Generating...',
    url: '',
    isLoading: true,
    generationStartTime: autoStartTime,
    presetType: selectedPreset.type as PreviewMediaType | undefined,
  };
  getAppStore().prependContentFile(placeholder);
  onPlaceholderCreated(placeholderUuid);

  if (isDesktop) {
    setAppStore({ rightPanelTab: 'creations' });
  } else {
    setAppStore({ screen: 'files' });
  }

  const { activeTabSlug } = getAppStore();
  const settings = {
    category: activeTabSlug || '',
    id: selectedPreset.slug || '',
  };
  const startTime = Date.now();
  sendGenerateStartAnalytics(settings);

  let submittedGenerationId: string | undefined;

  const resetAutoGeneration = () => {
    const { contentFiles, setContentFiles } = getAppStore();
    setContentFiles(contentFiles.filter((f) => f.uuid !== placeholderUuid));
    if (submittedGenerationId) {
      clearGeneration(submittedGenerationId);
    }
    onReset();
  };

  try {
    const result = await generationPipeline({ webhookId });

    if (!result?.canProcess) {
      resetAutoGeneration();
      return;
    }

    setAppStore({
      projectCreditsCount: result.projectCreditsCount ?? null,
    });

    // Build params and submit webhook
    const webhookParams = buildParamsFromInputs(inputs);
    const webhookResponse = await submitWebHook({
      webhookId,
      params: webhookParams,
      driveOptions: {
        folderName,
        packageId: toolId,
      },
      notificationConfig: createNotificationConfig(),
    });
    submittedGenerationId = webhookResponse.id;

    await saveGeneration({
      generationId: webhookResponse.id,
      placeholderUuid,
      presetTitle: selectedPreset.title || '',
      startedAt: autoStartTime,
      presetType: selectedPreset.type,
    });

    // Poll for result
    const flowMedia = await getMediaFromFlowId(webhookResponse.id);
    if (!flowMedia) {
      setGenerationError('Failed to get generation result');
      showToast('Failed to get generation result', 'error');
      sendGenerateFailureAnalytics(
        Date.now() - startTime,
        settings,
        'Generation result is empty'
      );
      resetAutoGeneration();
      return;
    }

    const presetTitle = selectedPreset.title ?? 'Generated';
    const resultType = flowMedia.type.toLowerCase();
    const mediaType = getMediaTypeFromString(resultType);
    const isVideo = mediaType === MediaType.VIDEO;
    const isAudio = mediaType === MediaType.AUDIO;
    const timestamp = Date.now();
    const extension = getExtensionFromResultType(resultType);
    const filename = `${presetTitle} - ${formatTimestamp(
      timestamp
    )}.${extension}`;

    // Rename file on Drive
    if (flowMedia.driveUid) {
      try {
        await changeDriveFileName({
          id: flowMedia.driveUid,
          name: filename,
        });
      } catch {
        // Failed to rename - non-critical
      }
    }

    // Update placeholder with real result
    getAppStore().updateContentFile({
      uuid: placeholderUuid,
      updates: {
        uuid: flowMedia.driveUid || placeholderUuid,
        url: flowMedia.url,
        name: filename,
        type: flowMedia.type,
        preview: {
          url: isVideo ? flowMedia.poster : isAudio ? undefined : flowMedia.url,
          width: flowMedia.width,
          height: flowMedia.height,
        },
        isLoading: false,
      },
    });

    await clearGeneration(webhookResponse.id);

    setAppStore({
      resultFile: flowMedia,
      generationTimestamp: timestamp,
      pendingLightboxFileId: flowMedia.driveUid || placeholderUuid,
      isResultMode: true,
    });
  } catch (error) {
    const { userMessage, reason } = getGenerationErrorMessage(error);
    setGenerationError(userMessage);
    showToast(userMessage, 'error');
    sendGenerateFailureAnalytics(
      Date.now() - startTime,
      settings,
      `Auto Generation ${reason}`
    );
    resetAutoGeneration();
  } finally {
    setAppStore({
      isPrimaryActionLoading: false,
      isGenerating: false,
    });
  }
};
